import { Stack } from "@mui/material";
import {
  StyledFormControl,
  StyledValidationText,
} from "Components/Shared/FormStyles";
import { RadioButtons as RadioButtonsInput } from "Components/Shared/Inputs/RadioButtons";
import type { CodeListItem } from "Models/ICodeListDto";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

type Props<
  TFormType extends FieldValues,
  TransformedFieldValues = TFormType,
> = {
  codeList: Array<CodeListItem>;
  name: FieldPath<TFormType>;
  control: Control<TFormType, unknown, TransformedFieldValues>;
  label?: string;
  useCodesAsLabels?: boolean;
  disabled?: boolean;
  row?: boolean;
  justifyContent?: "center" | "flex-start";
  onChangeValueTransform?: (value: string) => unknown;
  size?: "small" | "medium";
  isErrorHidden?: boolean;
  isInitiallyUndefined?: boolean;
  onAfterChange?: (value: string) => void;
};

export const RadioButtons = <T extends object, V extends object>({
  codeList,
  name,
  control,
  useCodesAsLabels,
  disabled,
  row,
  justifyContent,
  label,
  onChangeValueTransform,
  size = "medium",
  isErrorHidden = false,
  isInitiallyUndefined,
  onAfterChange,
}: Props<T, V>) => {
  const defaultValue = codeList[0]?.code;

  return (
    <StyledFormControl>
      <Controller
        control={control}
        defaultValue={isInitiallyUndefined ? undefined : (defaultValue as any)}
        name={name}
        render={({
          field: { value, name, onChange, onBlur, ref },
          fieldState: { error },
        }) => (
          <Stack width="100%" display="flex" textAlign="center">
            <RadioButtonsInput
              label={label}
              codeList={codeList}
              value={value?.toString() ?? null}
              onChange={(val) => {
                onChange(
                  onChangeValueTransform ? onChangeValueTransform(val) : val,
                );
                onAfterChange?.(val);
              }}
              onBlur={onBlur}
              name={name}
              useCodesAsLabels={useCodesAsLabels}
              disabled={disabled}
              row={row}
              inputRef={ref}
              justifyContent={justifyContent}
              size={size}
            />
            {!isErrorHidden && (
              <StyledValidationText>{error?.message}</StyledValidationText>
            )}
          </Stack>
        )}
      />
    </StyledFormControl>
  );
};
